import React from 'react';
import toastr from 'react-hot-toast';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Typography } from '@mui/material';
import { useDropzone } from 'react-dropzone';
import { DndContext, closestCenter } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { EditorModal } from 'sharedComponents/ImageEditor';
import { Upload } from 'components/SVG';
import { useDialog } from 'hooks/common';
import type { Accept } from 'react-dropzone';
import type { DragEndEvent } from '@dnd-kit/core';
import type { BoxProps } from '@mui/material';
import type { SparkGalleryImagesType } from 'sharedComponents/SparkForm/SparkFormImageGallery';
import './SparkImageGallery.css';
import SparkImageLoader from '../SparkImageLoader';

const acceptedFileTypes: Accept = {
  'image/png': ['.png'],
  'image/svg+xml': ['.svg'],
  'image/jpg': ['.jpg'],
  'image/jpeg': ['.jpeg'],
};

type SparkImageUploaderNewProps = {
  onSave: (image: SparkGalleryImagesType[]) => void;
  images: SparkGalleryImagesType[];
  maxImagesCount?: number;
  containerStyles?: BoxProps['style'];
  dropboxStyles?: BoxProps['style'];
};

function SparkImageGallery({
  onSave,
  images,
  maxImagesCount = 10,
  containerStyles = {},
  dropboxStyles = {},
}: SparkImageUploaderNewProps) {
  const onDrop = React.useCallback(
    (acceptedFiles: File[]) => {
      if (
        acceptedFiles.length > maxImagesCount ||
        images.length + acceptedFiles.length > maxImagesCount
      ) {
        toastr.error('Max of five images are accepted');
        return;
      }
      const imagePreviews = acceptedFiles.map((file) =>
        Object.assign(file, {
          preview: URL.createObjectURL(file),
        })
      );
      onSave([...images, ...imagePreviews]);
    },
    [images, maxImagesCount, onSave]
  );
  const [croppedImage, setCroppedImage] = React.useState<{
    url: string;
    imageIndex: number;
  }>();

  const {
    open: isEditorModalOpen,
    openDialog: openEditorModal,
    closeDialog: closeEditorModal,
  } = useDialog();

  const handleRemoveImage = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>, indexToRemove: number) => {
      event.stopPropagation();
      const updatedImages = images.filter(
        (_, index) => index !== indexToRemove
      );
      onSave(updatedImages);
    },
    [images, onSave]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: acceptedFileTypes,
  });

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = images.findIndex((img) => img.preview === active.id);
      const newIndex = images.findIndex((img) => img.preview === over?.id);
      const movedArray = arrayMove(images, oldIndex, newIndex);
      onSave(movedArray);
    }
  };

  const handleCropImage = (
    event: React.MouseEvent<HTMLButtonElement>,
    imageIndex: number
  ) => {
    event.stopPropagation();

    fetch(images[imageIndex].preview)
      .then((response) => response.blob())
      .then((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          setCroppedImage({ url, imageIndex });
          openEditorModal();
        } else {
          console.error('Error: Failed to fetch image data.');
        }
      })
      .catch((error) => console.error('Error:', error));
  };

  const getCroppedImage = React.useCallback(
    async (file: File | null | undefined, srcUrl: string) => {
      if (!file) {
        return;
      }
      const newObj = Object.assign(file, { preview: srcUrl });
      if (croppedImage?.imageIndex !== undefined) {
        const imagesDupli = [...images];
        imagesDupli.splice(croppedImage.imageIndex, 1, newObj);
        onSave(imagesDupli);
      }
    },
    [croppedImage?.imageIndex, images, onSave]
  );

  const PreviewBoxWithImages = ({ index }: { index: number }) => {
    const { attributes, listeners, setNodeRef, transform, transition } =
      useSortable({ id: images[index]?.preview || `empty-${index}` });

    const previewBoxStyle = {
      transform: CSS.Transform.toString(transform) ?? '',
      transition: transition ?? '',
    };

    return (
      <Box key={index} className='previewBox' sx={previewBoxStyle}>
        <Box className='previewWrapper'>
          <img
            src={images[index].preview}
            alt='Preview'
            className='previewImage'
            loading='lazy'
            {...attributes}
            {...listeners}
            ref={setNodeRef}
          />
          <button
            type='button'
            onClick={(event) => {
              handleRemoveImage(event, index);
            }}
            className='removeButton'
          >
            <CloseIcon fontSize='inherit' />
          </button>
          <button
            className='editButton'
            type='button'
            onClick={(event) => handleCropImage(event, index)}
          >
            <ContentCutIcon fontSize='inherit' />
          </button>
        </Box>
      </Box>
    );
  };

  const RenderPreviewBoxes = ({ index }: { index: number }) => {
    return <Box key={index} className='previewBox'></Box>;
  };

  return (
    <>
      <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext
          items={images.map((image) => image.preview)}
          strategy={horizontalListSortingStrategy}
        >
          <Box sx={{ marginBottom: '10px', width: '100%' }}>
            <Box sx={{ ...containerStyles }} className='container'>
              <Box
                className='dropzone'
                sx={{
                  ...dropboxStyles,
                  cursor: images[0] ? 'default' : 'pointer',
                }}
              >
                {images[0] ? (
                  <SparkImageLoader
                    src={images[0].preview}
                    alt='Preview'
                    className='previewImageDropzone'
                  />
                ) : (
                  <Box {...getRootProps()} className='dropzoneText'>
                    <input type='file' {...getInputProps()} />
                    <Upload />
                    <Box sx={{ marginTop: '10px' }}>
                      <Typography
                        component='span'
                        sx={{ color: (theme) => theme.palette.primary.main }}
                      >
                        Browse for files
                      </Typography>{' '}
                      or{' '}
                      <Typography
                        component='span'
                        sx={{ color: (theme) => theme.palette.primary.main }}
                      >
                        Paste a URL
                      </Typography>
                    </Box>
                    <Typography variant='body2'>
                      Upload at least one image
                    </Typography>
                    <Typography variant='body2'>
                      1270x760px or higher recommended
                    </Typography>
                    <Typography variant='body2'>
                      The first image will be used as preview
                    </Typography>
                  </Box>
                )}
              </Box>
              <Box className='previewGrid'>
                {images.length ? (
                  <>
                    {images.map((_image, index) => (
                      <PreviewBoxWithImages index={index} />
                    ))}
                    {images.length !== maxImagesCount ? (
                      <Box
                        className='previewBox'
                        sx={{ cursor: 'pointer' }}
                        {...getRootProps()}
                      >
                        <input type='file' {...getInputProps()} />
                        <Box className='emptyBox'>+</Box>
                      </Box>
                    ) : (
                      <></>
                    )}
                  </>
                ) : (
                  <Box className='previewGrid'>
                    {Array.from({ length: maxImagesCount }).map((_, index) => {
                      return <RenderPreviewBoxes index={index} key={index} />;
                    })}
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </SortableContext>
      </DndContext>
      {isEditorModalOpen && croppedImage && (
        <EditorModal
          onClose={closeEditorModal}
          open={isEditorModalOpen}
          imageSrc={croppedImage.url}
          aspectRatio={3 / 2}
          getCroppedImage={getCroppedImage}
        />
      )}
    </>
  );
}

export default SparkImageGallery;
